import React from 'react';

import { Row } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';

interface LinkButtonProps {
  children: React.ReactNode;
  /** Required: the primitive's onClick mode has no other accessible name. */
  'aria-label': string;
  analyticsName: string;
  /** Typography and width belong to the caller; this owns the colour. */
  className?: string;
  onClick: () => void;
  /**
   * `primary` is brand-coloured and unadorned, `secondary` is body-coloured and
   * underlined. Both go brand on hover.
   */
  variant?: 'primary' | 'secondary';
}

/**
 * A text action that looks like a link but is a real `<button>`.
 *
 * Atoms has no component for this shape: `Link` needs an `href` (an href-less anchor trips
 * `jsx-a11y/anchor-is-valid`) and `Button` has no text/ghost variant. The layout primitive's
 * `onClick` mode renders `<button type="button">` and fires a `buttonClickEvent`.
 */
export const LinkButton: React.FC<LinkButtonProps> = ({
  children,
  'aria-label': ariaLabel,
  analyticsName,
  className,
  onClick,
  variant = 'primary',
}) => (
  <Row
    onClick={onClick}
    analyticsName={analyticsName}
    aria-label={ariaLabel}
    className={cn(
      'cursor-pointer select-none hover:text-od-content-hover-accent-brand',
      variant === 'primary' ? 'text-od-content-accent-brand' : 'text-od-content-primary underline',
      className,
    )}
  >
    {children}
  </Row>
);
